package fr.manyverse.app.presentation.components.modules.whiteboard

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Draw
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.alpha
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.dp
import fr.manyverse.app.domain.model.WhiteboardColor
import manyverse.shared.generated.resources.Res
import manyverse.shared.generated.resources.icon_eraser
import manyverse.shared.generated.resources.whiteboard_eraser
import manyverse.shared.generated.resources.whiteboard_pen
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource

/**
 * Toolbar for the whiteboard GM controls.
 */
@Composable
fun WhiteboardGmToolbar(
    selectedColor: WhiteboardColor,
    selectedTool: WhiteboardTool,
    onColorSelect: (WhiteboardColor) -> Unit,
    onToolSelect: (WhiteboardTool) -> Unit,
    modifier: Modifier = Modifier
) {
    Surface(
        modifier = modifier,
        shape = RoundedCornerShape(12.dp),
        shadowElevation = 4.dp,
        color = MaterialTheme.colorScheme.surface
    ) {
        Row(
            modifier = Modifier.padding(8.dp),
            horizontalArrangement = Arrangement.spacedBy(4.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            // Tool selector
            IconButton(
                onClick = { onToolSelect(WhiteboardTool.ERASER) },
            ) {
                Icon(
                    painter = painterResource(Res.drawable.icon_eraser),
                    contentDescription = stringResource(Res.string.whiteboard_eraser),
                    tint = toolTint(isSelected = selectedTool == WhiteboardTool.ERASER)
                )
            }

            IconButton(
                onClick = { onToolSelect(WhiteboardTool.PEN) },
            ) {
                Icon(
                    imageVector = Icons.Default.Draw,
                    contentDescription = stringResource(Res.string.whiteboard_pen),
                    tint = toolTint(isSelected = selectedTool == WhiteboardTool.PEN)
                )
            }

            Spacer(modifier = Modifier.width(8.dp))

            // Color palette
            WhiteboardColor.palette.forEach { color ->
                ColorButton(
                    color = color,
                    isSelected = color == selectedColor && selectedTool == WhiteboardTool.PEN,
                    onClick = { onColorSelect(color) },
                    modifier = Modifier.alpha(
                        if (selectedTool == WhiteboardTool.PEN) 1f else 0.4f
                    )
                )
            }
        }
    }
}

@Composable
private fun toolTint(isSelected: Boolean): Color =
    if (isSelected) {
        MaterialTheme.colorScheme.primary
    } else {
        MaterialTheme.colorScheme.onSurface
    }

@Composable
private fun ColorButton(
    color: WhiteboardColor,
    isSelected: Boolean,
    onClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    Box(
        modifier = modifier
            .size(32.dp)
            .clip(CircleShape)
            .background(Color(color.hexValue))
            .then(
                if (isSelected) {
                    Modifier.border(
                        width = 3.dp,
                        color = MaterialTheme.colorScheme.primary,
                        shape = CircleShape
                    )
                } else {
                    Modifier.border(
                        width = 1.dp,
                        color = MaterialTheme.colorScheme.outline,
                        shape = CircleShape
                    )
                }
            )
            .clickable(onClick = onClick)
    )
}
